import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  PROCEDURES_ERROR_CODES,
  PROCEDURES_RUN_STATUS,
  useProceduresServices,
  type ProcedureRunView,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { MissingRunTokenError, runActions } from '@/lib/portalApi';
import { proceduresKeys } from '@/lib/queryKeys';
import { errorMessage, errorStatus, isRetryable, procedureErrorCode } from '@/lib/errors';
import { utcStamp } from '@/lib/format';
import { clearInProgress, getRunRecord, markRunFinished, saveRunSnapshot, setInProgress, updateRunRecord } from '@/lib/runStore';
import {
  backoffMs,
  dropRun,
  markSynced,
  patchEntry,
  reloadFromStorage,
  removeEntry,
  reportProblem,
  setInFlight,
  setOnline,
  useSyncStore,
  wakeSync,
  type QueueEntry,
} from '@/lib/syncQueue';

type Outcome = 'sent' | 'wait' | 'dropped';

/** Retry a waiting queue at least this often, even if the browser never fires `online`. */
const POLL_MS = 20_000;

/**
 * Sends the offline queue (lib/syncQueue.ts) to the API: in order per run, one request at a time,
 * with backoff while there is no signal. Mounted once inside the providers; renders nothing.
 *
 * - Network failures, 5xx, 408 and 429 stay queued and retry (2 s doubling to 60 s, plus a 20 s poll
 *   and the browser's `online` event).
 * - Signed-in entries wait while nobody is signed in (sign in again and they go).
 * - run_closed / run_token_invalid drop that run's queue; other refusals drop the one entry and show
 *   a short message in the sync bar.
 */
export function SyncEngine() {
  const services = useProceduresServices();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useEnrichedAuth();
  const wake = useSyncStore((s) => s.wake);
  const hasEntries = useSyncStore((s) => s.entries.length > 0);
  const runningRef = useRef(false);
  const timerRef = useRef<number | undefined>(undefined);
  const authRef = useRef(isAuthenticated);
  authRef.current = isAuthenticated;

  const afterSuccess = useCallback(
    (entry: QueueEntry, view: ProcedureRunView) => {
      saveRunSnapshot(view);
      queryClient.setQueryData(proceduresKeys.run(entry.runId), view);
      if (view.statusID === PROCEDURES_RUN_STATUS.Completed) markRunFinished(entry.runId, 'completed');
      if (view.statusID === PROCEDURES_RUN_STATUS.Abandoned) markRunFinished(entry.runId, 'abandoned');
      if (entry.kind !== 'step') {
        void queryClient.invalidateQueries({ queryKey: proceduresKeys.myRuns() });
        void queryClient.invalidateQueries({ queryKey: ['procedures', 'view'] });
      }
      markSynced();
    },
    [queryClient],
  );

  const sendOne = useCallback(
    async (entry: QueueEntry): Promise<Outcome> => {
      setInFlight(entry.id);
      try {
        const actions = runActions(services, entry.mode);
        let view: ProcedureRunView;
        if (entry.kind === 'step' && entry.stepId !== undefined && entry.step) {
          const req = {
            ...entry.step,
            clientCheckedAt: entry.step.clientCheckedAt ?? (entry.step.statusID ? utcStamp(entry.createdAt) : null),
          };
          view = await actions.updateStep(entry.runId, entry.stepId, req);
        } else if (entry.kind === 'complete') {
          view = await actions.complete(entry.runId, entry.finish ?? {});
        } else if (entry.kind === 'abandon') {
          view = await actions.abandon(entry.runId, entry.finish ?? {});
        } else {
          setInFlight(null);
          removeEntry(entry.id);
          return 'dropped';
        }
        setInFlight(null);
        removeEntry(entry.id);
        afterSuccess(entry, view);
        return 'sent';
      } catch (err) {
        setInFlight(null);
        const code = procedureErrorCode(err);
        const record = getRunRecord(entry.runId);
        const title = record?.title ? `"${record.title}"` : 'this procedure';

        if (err instanceof MissingRunTokenError || code === PROCEDURES_ERROR_CODES.RunTokenInvalid) {
          dropRun(entry.runId);
          updateRunRecord(entry.runId, { state: 'lost', finishedAtMs: Date.now() });
          clearInProgress(entry.code, entry.runId);
          reportProblem({
            runId: entry.runId,
            code: entry.code,
            message: `We couldn't save your progress on ${title} because this phone no longer has its run. Please start it again.`,
          });
          return 'dropped';
        }
        if (code === PROCEDURES_ERROR_CODES.RunClosed) {
          // Already finished on the server (done on another phone, or closed after being left idle).
          dropRun(entry.runId);
          markRunFinished(entry.runId, entry.kind === 'abandon' ? 'abandoned' : 'completed');
          if (entry.kind === 'step') {
            reportProblem({
              runId: entry.runId,
              code: entry.code,
              message: `${title.charAt(0).toUpperCase()}${title.slice(1)} was already closed, so your last ticks couldn't be saved.`,
            });
          }
          void queryClient.invalidateQueries({ queryKey: proceduresKeys.run(entry.runId) });
          return 'dropped';
        }
        const status = errorStatus(err);
        if (isRetryable(err) || status === 401) {
          const attempts = entry.attempts + 1;
          patchEntry(entry.id, {
            attempts,
            nextAttemptAt: Date.now() + backoffMs(attempts),
            lastError: errorMessage(err),
          });
          return 'wait';
        }
        // Refused for good (validation, access): drop this one change and say so.
        removeEntry(entry.id);
        if (entry.kind === 'complete') {
          // Not finished after all: offer "Carry on" again when the QR code is scanned.
          updateRunRecord(entry.runId, { state: 'active', finishedAtMs: null });
          setInProgress(entry.code, entry.runId);
        }
        reportProblem({ runId: entry.runId, code: entry.code, message: `One change on ${title} couldn't be saved. ${errorMessage(err)}` });
        return 'dropped';
      }
    },
    [services, afterSuccess, queryClient],
  );

  const schedule = useCallback((at: number) => {
    window.clearTimeout(timerRef.current);
    if (!Number.isFinite(at)) return;
    const delay = Math.max(250, Math.min(POLL_MS, at - Date.now()));
    timerRef.current = window.setTimeout(() => wakeSync(), delay);
  }, []);

  const drain = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      for (let guard = 0; guard < 100; guard += 1) {
        const { entries } = useSyncStore.getState();
        if (entries.length === 0) return;
        const now = Date.now();
        const seenRuns = new Set<number>();
        let next: QueueEntry | null = null;
        let soonest = Number.POSITIVE_INFINITY;
        // Strict order per run: only the oldest entry of each run may go.
        for (const e of entries) {
          if (seenRuns.has(e.runId)) continue;
          seenRuns.add(e.runId);
          if (e.mode === 'member' && !authRef.current) continue;
          if (e.nextAttemptAt > now) {
            soonest = Math.min(soonest, e.nextAttemptAt);
            continue;
          }
          next = e;
          break;
        }
        if (!next) {
          schedule(soonest);
          return;
        }
        const outcome = await sendOne(next);
        if (outcome === 'wait') {
          // One failure usually means no signal: pause everything and let the backoff timer retry.
          const after = useSyncStore.getState().entries.find((e) => e.id === next?.id);
          schedule(after?.nextAttemptAt ?? now + POLL_MS);
          return;
        }
      }
      schedule(Date.now() + 500);
    } finally {
      runningRef.current = false;
    }
  }, [schedule, sendOne]);

  // Wake on: queue changes, sign-in changes, coming back online, the app coming to the front.
  useEffect(() => {
    void drain();
  }, [wake, isAuthenticated, drain]);

  useEffect(() => {
    const online = () => {
      setOnline(true);
      // A fresh signal: retry now instead of waiting out the backoff.
      useSyncStore.getState().entries.forEach((e) => {
        if (e.nextAttemptAt > Date.now()) patchEntry(e.id, { nextAttemptAt: Date.now() });
      });
    };
    const offline = () => setOnline(false);
    const visible = () => {
      if (document.visibilityState === 'visible') wakeSync();
    };
    const storage = (e: StorageEvent) => {
      if (e.key === 'procedures.queue') reloadFromStorage();
    };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('storage', storage);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('storage', storage);
      window.clearTimeout(timerRef.current);
    };
  }, []);

  // Safety net poll while anything is waiting.
  useEffect(() => {
    if (!hasEntries) return undefined;
    const id = window.setInterval(() => wakeSync(), POLL_MS);
    return () => window.clearInterval(id);
  }, [hasEntries]);

  return null;
}
